import { logger } from '@damofire/data-commons';
import { clone } from 'lodash-es';
import { Observer, Subject } from 'rxjs';
import { WarehouseSyncStep, WarehouseSyncStepContext, WarehouseSyncStepContextEvent, WarehouseSyncStepParams, WarehouseSyncStepStatus, WarehouseSyncStepTask } from '../executors';
import { getErrorMessage } from './steps-utils';
import { WarehouseSyncStepFactory } from './warehouse-steps.factory';
import { WarehouseSyncStepTaskExecutor } from './warehouse-tasks.executor';

export class WarehouseSyncStepExecutor {
  private shouldCancel = false;
  private runningTaskBreaker: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly factory: WarehouseSyncStepFactory,
    private readonly taskExec: WarehouseSyncStepTaskExecutor,
  ) {}

  async executeStep(
    params: WarehouseSyncStepParams,
    syncId: string,
    step: WarehouseSyncStep,
    listener: Observer<WarehouseSyncStepContextEvent>,
  ): Promise<WarehouseSyncStepContext> {
    const { division, environment, userPreferences } = params;
    logger.info(`Starting ${step} for warehouse sync`, { category: 'sync', division });
    const tasks = this.factory.createTasks(step, environment, userPreferences);
    const status = tasks.reduce(
      (s, t, inx) => {
        s[inx] = 'PENDING';
        return s;
      },
      {} as Record<number, WarehouseSyncStepStatus>,
    );

    const when = new Date().getTime();
    const ctx: WarehouseSyncStepContext = {
      _id: `${when}`,
      syncId,
      step,
      task: 'none',
      tasks: tasks.length,
      taskInx: 0,
      division,
      status,
      when,
    };

    try {
      for (let taskInx = 0; taskInx < tasks.length && !this.shouldCancel; ++taskInx) {
        await this.executeTask(clone(ctx), tasks[taskInx], taskInx, listener);
      }
    } catch (err) {
      logger.error('Cannot complete warehouse sync step', { category: 'sync', division, step, error: err });

      if (!this.shouldCancel) {
        ctx.when = new Date().getTime();
        listener.next({ ctx: clone(ctx), event: 'STEP_ERROR', err: getErrorMessage(err) });
      }
    } finally {
      if (!this.shouldCancel) {
        ctx.when = new Date().getTime();
        listener.next({ ctx: clone(ctx), event: 'STEP_FINISHED' });
      }
    }

    return ctx;
  }

  cancelSync(): void {
    this.shouldCancel = true;
    this.runningTaskBreaker.next(true);
  }

  /**
   * Runs a single task in isolation (diagnostics / targeted re-run).
   * Only meaningful for push/download tasks: clean tasks delete every row
   * whose syncId differs from the run's, so running one alone would wipe
   * the store. Callers must enforce that (see forceStartTask).
   */
  async executeSingleTask(
    params: WarehouseSyncStepParams,
    syncId: string,
    step: WarehouseSyncStep,
    taskName: string,
    listener: Observer<WarehouseSyncStepContextEvent>,
  ): Promise<WarehouseSyncStepContext> {
    return this.executeTasks(params, syncId, step, [taskName], listener);
  }

  /** Runs several tasks of one step sequentially (e.g. picture listing then picture bytes). Same clean-step caveat as executeSingleTask. */
  async executeTasks(
    params: WarehouseSyncStepParams,
    syncId: string,
    step: WarehouseSyncStep,
    taskNames: string[],
    listener: Observer<WarehouseSyncStepContextEvent>,
  ): Promise<WarehouseSyncStepContext> {
    const { division } = params;
    logger.info(`Starting single tasks ${taskNames.join(',')} for warehouse sync`, { category: 'sync', division, step });
    const tasks = this.factory.createTasks(step, params.environment, params.userPreferences);
    const found = taskNames.map((name) => ({ name, inx: tasks.findIndex((t) => t.name() === name) }));
    const missing = found.filter((f) => f.inx < 0).map((f) => f.name);

    const when = Date.now();
    const ctx: WarehouseSyncStepContext = {
      _id: `${when}`,
      syncId,
      step,
      task: 'none',
      tasks: tasks.length,
      taskInx: 0,
      division,
      status: {},
      when,
    };

    try {
      if (!found.some((f) => f.inx >= 0) || this.shouldCancel) {
        throw new Error(`Tasks ${missing.join(',')} not found for step ${step}`);
      }
      if (missing.length) {
        logger.error('Some single tasks not found, running the rest', { category: 'sync', division, step, missing });
      }
      for (const f of found.filter((f) => f.inx >= 0)) {
        if (this.shouldCancel) {
          break;
        }
        await this.executeTask(clone(ctx), tasks[f.inx], f.inx, listener);
      }
    } catch (err) {
      logger.error('Cannot complete single warehouse sync tasks', { category: 'sync', division, step, tasks: taskNames, error: err });

      if (!this.shouldCancel) {
        ctx.when = Date.now();
        listener.next({ ctx: clone(ctx), event: 'STEP_ERROR', err: getErrorMessage(err) });
      }
    } finally {
      if (!this.shouldCancel) {
        ctx.when = Date.now();
        listener.next({ ctx: clone(ctx), event: 'STEP_FINISHED' });
      }
    }

    return ctx;
  }

  private async executeTask(ctx: WarehouseSyncStepContext, task: WarehouseSyncStepTask, taskInx: number, listener: Observer<WarehouseSyncStepContextEvent>): Promise<void> {
    logger.info(`Starting task ${task.name()}`, { category: 'sync', taskInx, division: ctx.division });
    ctx.task = task.name();
    ctx.status[taskInx] = 'RUNNING';
    ctx.taskInx = taskInx;
    ctx.when = new Date().getTime();
    listener.next({ ctx: clone(ctx), event: 'STEP_PROGRESS' });

    const taskExecutionContext = await this.taskExec.executeStepTask(task, taskInx, ctx, listener, this.runningTaskBreaker);
    logger.info(`Task ${task.name()} finished`, { category: 'sync', taskInx, status: taskExecutionContext?.status });

    if (taskExecutionContext) {
      ctx.status[taskInx] = taskExecutionContext.status;
    }

    ctx.when = new Date().getTime();
    const event = this.shouldCancel ? 'STEP_CANCELLED' : 'STEP_PROGRESS';
    listener.next({ ctx: clone(ctx), event });
  }
}
